import React from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import { observable, action, makeObservable, computed } from 'mobx';
import { Label } from 'reactstrap';
import { FaFilter, FaSortAlphaDown, FaSortAlphaDownAlt } from 'react-icons/fa';

import { Checkbox } from '@Components';
import './filter-popup.scss';

export class ColumnSort {
    public static readonly ASC: string = 'asc';
    public static readonly DESC: string = 'desc';
}
export class ColumnFilters {
    public static readonly WithValues: string = 'with values';
    public static readonly WithoutValues: string = 'without values';
}

export type FilterValue = string | number | ColumnFilters;
export type FilterOption = { name: string | number, value: FilterValue, isGroup?: boolean, children?: FilterOption[] };
export type ColumnFiltersMap = Map<string, FilterValue[]>;
export type ColumnSortModel = { sortType: ColumnSort, key: string };

type FilterPopupProps = {
    dataField: string;
    filterOptions?: FilterOption[] | (() => FilterOption[])
    isFilterActive: boolean;
    disableSorting?: boolean;
    columnSort: ColumnSortModel | null;
    columnFilters: ColumnFiltersMap;
    onChangeSort: (key: string, sortType: ColumnSort) => void;
    onChangeFilters: (key: string, filterValue: FilterValue) => void;
};

@observer
export class FilterPopup extends React.Component<FilterPopupProps, {}> {
    @observable private _isVisible: boolean = false;
    @observable private _popupEvent: React.MouseEvent | null = null;
    private _popupWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();

    constructor(props: FilterPopupProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this._handleClickOutside);
        window.addEventListener('resize', this._onDisappear);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._handleClickOutside);
        window.removeEventListener('resize', this._onDisappear);
    }

    @action.bound
    private _onDisappear() {
        this._isVisible = false;
    }

    @action.bound
    private _handleClickOutside(event: MouseEvent) {
        if (this._isVisible && !this._popupWrapperRef.current?.contains(event.target as Node)) {
            this._onDisappear();
        }
    }

    render() {
        const clickX = this._popupEvent?.clientX || 100;
        const clickY = this._popupEvent?.clientY || 100;

        const screenW = window.innerWidth;
        const popupWidth = 120;

        let left = clickX + 5;
        let top = clickY + 5;

        if ((screenW - clickX) < popupWidth) {
            left = clickX - popupWidth - 5;
        }

        const maxHeight = this._getMaxPopupHeight(top);
        const { isFilterActive } = this.props;
        const portalTarget = typeof document !== 'undefined' ? document.body : null;

        return (
            <>
                <FaFilter
                    className={`filter-icon ${isFilterActive ? 'active' : ''}`}
                    onClick={this._toggleFilterPopupHandler}
                />
                {this._isVisible && portalTarget && ReactDOM.createPortal(
                    <div ref={this._popupWrapperRef} className="filter-popup" style={{ left, top, maxHeight }}>
                        {this._renderSortOptions()}
                        {this._renderFilterOptions()}
                    </div>,
                    portalTarget
                )}
            </>
        );
    }

    @action.bound
    private _toggleFilterPopupHandler(e: React.MouseEvent) {
        this._popupEvent = e;
        this._isVisible = true;
    }

    private _renderSortOptions() {
        const { columnSort, dataField, onChangeSort } = this.props;

        return this._sortOptions.map((s) => {
            const isActive = columnSort?.key === dataField && columnSort?.sortType === s.value;
            return (
                <div
                    key={s.value}
                    className={`sort-item-wrapper${isActive ? ' active' : ''}`}
                    onClick={() => onChangeSort(dataField, s.value)}
                >
                    {s.icon}
                    <span >{s.name}</span>
                </div>
            );
        });
    }

    private _renderFilterOptions() {
        const { columnFilters, dataField, onChangeFilters } = this.props;

        return this._filterOptions.map(f => {
            const isChecked = !!columnFilters.get(dataField)?.includes(f.value);
            return (
                <Label
                    key={f.value.toString()}
                    className="filter-item-wrapper"
                >
                    <Checkbox
                        style={{ width: 'unset', height: 'unset' }}
                        checked={isChecked}
                        onChange={() => onChangeFilters(dataField, f.value)}
                    />
                    <span>{f.name}</span>
                </Label>
            );
        });
    }

    private _getMaxPopupHeight(popupTop: number) {
        return window.innerHeight - popupTop - 100;
    }

    @computed
    private get _sortOptions() {
        if (this.props.disableSorting) return [];

        return [
            { name: 'Sort ascending', value: ColumnSort.ASC, icon: <FaSortAlphaDown /> },
            { name: 'Sort descending', value: ColumnSort.DESC, icon: <FaSortAlphaDownAlt /> }
        ];
    }

    @computed
    private get _filterOptions() {
        const defaultOptions = [
            { name: 'With values', value: ColumnFilters.WithValues },
            { name: 'Without values', value: ColumnFilters.WithoutValues }
        ];
        
        const options = (typeof this.props.filterOptions === 'function' ? this.props.filterOptions() : this.props.filterOptions) || [];

        return [...defaultOptions, ...options];
    }
}
